import { Region, RegionShippingService, ShippingService } from '../types'

// Buyer-country shipping presets. Only Japan is filled in.
// Other countries stay empty until sellers can opt into international rates.
// Bands are published carrier guidance as of 2026-10-01. The seller stores the fee.

export const JAPAN_COUNTRY_CODE = 'JP'
export const JAPAN_REGION_ID = 3

export const SHIPPING_SERVICE_ID = {
  nekopos: 6,
  yupack: 20,
  letterPackLight: 21,
  letterPackPlus: 22,
} as const

export type ShippingFeeBand = Readonly<{
  minJpy: number
  maxJpy: number
}>

export type ShippingPreset = Readonly<{
  shippingServiceId: number
  label: string
  buyerCountryCodes: readonly string[]
  feeBand: ShippingFeeBand
  recommendedFeeJpy: number
}>

export const shippingPresets: readonly ShippingPreset[] = [
  {
    shippingServiceId: SHIPPING_SERVICE_ID.nekopos,
    label: 'nekopost',
    buyerCountryCodes: [JAPAN_COUNTRY_CODE],
    // Yamato publishes a nationwide cap. Contract rates can be lower, so the seller can change the fee.
    feeBand: { minJpy: 385, maxJpy: 385 },
    recommendedFeeJpy: 385,
  },
  {
    shippingServiceId: SHIPPING_SERVICE_ID.letterPackLight,
    label: 'letterpacklight',
    buyerCountryCodes: [JAPAN_COUNTRY_CODE],
    feeBand: { minJpy: 430, maxJpy: 430 },
    recommendedFeeJpy: 430,
  },
  {
    shippingServiceId: SHIPPING_SERVICE_ID.letterPackPlus,
    label: 'letterpackplus',
    buyerCountryCodes: [JAPAN_COUNTRY_CODE],
    feeBand: { minJpy: 600, maxJpy: 600 },
    recommendedFeeJpy: 600,
  },
  {
    shippingServiceId: SHIPPING_SERVICE_ID.yupack,
    label: 'yupack',
    buyerCountryCodes: [JAPAN_COUNTRY_CODE],
    // 60-size Yu-Pack basic fare: same prefecture ¥840 through distant routes about ¥1,930.
    feeBand: { minJpy: 840, maxJpy: 1930 },
    recommendedFeeJpy: 1090,
  },
]

export type PresetFeeHint = Readonly<{
  key: 'account.sell.shipping_options.fee_band' | 'account.sell.shipping_options.fee_suggested'
  values: Readonly<{ min?: string, max?: string, fee?: string }>
}>

type JapanShippingAddress = Readonly<{
  countryCode?: string
  regionId?: number
}>

const groupDigits = (amount: number): string => {
  return Math.round(amount).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

const presetsForBuyerCountry = (countryCode?: string): readonly ShippingPreset[] => {
  if (!countryCode) return []

  return shippingPresets.filter(preset => preset.buyerCountryCodes.includes(countryCode))
}

const presetForShippingService = (shippingServiceId?: number): ShippingPreset | undefined => {
  if (shippingServiceId == null) return

  return shippingPresets.find(preset => preset.shippingServiceId === shippingServiceId)
}

const sellerFeeForPreset = (preset: ShippingPreset, customFeeJpy?: number): number => {
  if (customFeeJpy == null || !Number.isFinite(customFeeJpy) || customFeeJpy < 0) {
    return preset.recommendedFeeJpy
  }

  return Math.round(customFeeJpy)
}

const presetToShippingService = (preset: ShippingPreset): ShippingService => {
  return {
    id: preset.shippingServiceId,
    label: preset.label,
    recommendedPrice: preset.recommendedFeeJpy,
    buyerCountryCodes: preset.buyerCountryCodes,
    suggestedFeeMinJpy: preset.feeBand.minJpy,
    suggestedFeeMaxJpy: preset.feeBand.maxJpy,
  }
}

const sellerShippingCatalog = (services: readonly ShippingService[]): ShippingService[] => {
  const presets = presetsForBuyerCountry(JAPAN_COUNTRY_CODE).map(presetToShippingService)
  const presetIds = new Set(presets.map(preset => preset.id))

  return [
    ...presets,
    ...services.filter(service => !presetIds.has(service.id)),
  ]
}

const presetFeeHint = (preset: ShippingPreset): PresetFeeHint => {
  if (preset.feeBand.minJpy === preset.feeBand.maxJpy) {
    return {
      key: 'account.sell.shipping_options.fee_suggested',
      values: { fee: groupDigits(preset.recommendedFeeJpy) },
    }
  }

  return {
    key: 'account.sell.shipping_options.fee_band',
    values: {
      min: groupDigits(preset.feeBand.minJpy),
      max: groupDigits(preset.feeBand.maxJpy),
    },
  }
}

const draftFromPreset = (preset: ShippingPreset, region: Region, customFeeJpy?: number): RegionShippingService => {
  return {
    id: 0,
    region,
    shippingService: presetToShippingService(preset),
    priceJpy: sellerFeeForPreset(preset, customFeeJpy),
    description: '',
  }
}

const isJapanShippingAddress = (address?: JapanShippingAddress): boolean => {
  if (!address) return false
  if (address.countryCode) return address.countryCode === JAPAN_COUNTRY_CODE

  return address.regionId === JAPAN_REGION_ID
}

export {
  draftFromPreset,
  isJapanShippingAddress,
  presetFeeHint,
  presetForShippingService,
  presetsForBuyerCountry,
  presetToShippingService,
  sellerFeeForPreset,
  sellerShippingCatalog,
}
